import * as React from "react";
import { useForm, useWatch, Controller, useFormContext as useOuterFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Step7TrailerSchema, parseCurrencyToNumber } from "../../utils/schema";
import { ViewContext } from "components/lib";
import { trailersBodytype } from "data";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../../../components/ui/dialog";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../../components/ui/select";
import { createTrailer, updateTrailerById, updateQuoteWithTrailers } from "./api";
import { getVehicleData } from "../../utils/getVehicleData";

export function TrailerUpsertDialog({
  open,
  onOpenChange,
  mode,                 // "create" | "edit"
  initial,
  companyId,
  quoteId,             // if present, update will patch the whole quote
  getWholeQuoteData,   // () => any (return full form payload that backend expects inside quote)
  onDone,              // (trailer: Trailer) => void
}) {
  const context = React.useContext(ViewContext);
  const [prevVin, setPrevVin] = React.useState(initial?.vin);
  const outer = useOuterFormContext();
  const carriersStep7 = useWatch({ control: outer.control, name: "step7.carrier" });
  const carriersStep1 = useWatch({ control: outer.control, name: "step1.carrier" });
  const carriers = (carriersStep7?.length ? carriersStep7 : carriersStep1) ?? [];
  const carriersNormalized = carriers.map((c) => c ?? "");
  const hasProgressive = carriersNormalized.includes("Progressive");
  const hasCoverWhale = carriersNormalized.includes("Cover Whale");
  const hasNirvana = carriersNormalized.includes("Nirvana");
  const hasCwOrNirvana = hasCoverWhale || hasNirvana;
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingValues, setPendingValues] = React.useState(null);
  const [confirmMessage, setConfirmMessage] = React.useState("");
  const removeCwNirvanaCarriers = () => {
    const filtered = carriersNormalized.filter(
      (c) => c !== "Cover Whale" && c !== "Nirvana"
    );
    outer.setValue("step1.carrier", filtered, { shouldDirty: true, shouldValidate: true });
    outer.setValue("step6.carrier", filtered, { shouldDirty: true, shouldValidate: true });
    outer.setValue("step7.carrier", filtered, { shouldDirty: true, shouldValidate: true });
  };

  const form = useForm({
    resolver: zodResolver(Step7TrailerSchema),
    defaultValues: mode === "edit" && initial ? {
      id: initial.id,
      type: "trailer",
      year: initial.year,
      make: initial.make,
      model: initial.model,
      vin: initial.vin,
      value: initial.value,
      bodyType: initial.bodyType,
      deductibleType: initial.deductibleType ?? "FollowsPolicy",
      deductible: initial.deductible ?? "1500",
    } : {
      type: "trailer",
      year: "",
      make: "",
      model: "",
      vin: "",
      value: "",
      bodyType: undefined,
      deductibleType: "FollowsPolicy",
      deductible: "1500",
    },
    mode: "all",
  });

  const { control, handleSubmit, formState: { isSubmitting }, watch } = form;

  const values = watch();
  const vin = useWatch({
    name:"vin",
    control,
    defaultValue: "",
  });
  const performSave = async (values) => {
    const payload = {
      type: "trailer",
      company_id: companyId,
      year: values.year,
      make: values.make,
      model: values.model,
      vin: values.vin,
      stated_value: String(parseCurrencyToNumber(values.value)),
      bodytype: values.bodyType,
    };

    if (mode === "create") {
      const created = await createTrailer(payload);
      const normalized = {
        id: created.id,
        type: "trailer",
        year: values.year,
        make: values.make,
        model: values.model,
        vin: values.vin,
        value: String(parseCurrencyToNumber(values.value)),
        bodyType: values.bodyType,
        deductibleType: "FollowsPolicy",
        deductible: "1500",
      };
      context.notification?.show?.("Trailer created successfully!", "success", true, "toast", "check-circle");
      onDone(normalized);
    } else {
      if (!initial?.trailerId) {
        context.notification?.show?.("Missing trailer id.", "error", true);
        return;
      }

      if (quoteId && getWholeQuoteData) {
        await updateQuoteWithTrailers(quoteId, getWholeQuoteData());
        const normalized = {
          ...initial,
          year: values.year,
          make: values.make,
          model: values.model,
          vin: values.vin,
          value: String(parseCurrencyToNumber(values.value)),
          bodyType: values.bodyType,
        };
        context.notification?.show?.("Trailer updated in quote", "success", true);
        onDone(normalized);
      } else {
        await updateTrailerById(initial.trailerId, payload);
        const normalized = {
          ...initial,
          year: values.year,
          make: values.make,
          model: values.model,
          vin: values.vin,
          value: String(parseCurrencyToNumber(values.value)),
          bodyType: values.bodyType,
        };
        context.notification?.show?.("Trailer updated", "success", true);
        onDone(normalized);
      }
    }

    onOpenChange(false);
    form.reset();
  };

  const getAgeInfo = (values) => {
    const age = new Date().getFullYear() - Number(values.year);
    const maxAge = 23;
    return { age, maxAge, overLimit: Number.isFinite(age) && age > maxAge };
  };

  const onSubmit = handleSubmit(async (values) => {
    const ageInfo = getAgeInfo(values);
    if (hasCwOrNirvana && ageInfo.overLimit) {
      const message = "Trailers above 23 years of age are not eligible";
      if (!hasProgressive) {
        form.setError("year", { type: "custom", message });
        return;
      }
      setPendingValues(values);
      setConfirmMessage(
        `This trailer is over ${ageInfo.maxAge} years old and not allowed to be quoted by Cover Whale/Nirvana. If you proceed, the carrier(s) will be eliminated from your quote.`
      );
      setConfirmOpen(true);
      return;
    }

    try {
      await performSave(values);
    } catch (err) {
      const msg =
        err?.response?.data?.message ?? (mode === "create" ? "Failed to create trailer" : "Failed to update trailer");
      context.notification?.show?.(msg, "error", true, "toast", "x-circle");
    }
  });

  React.useEffect(() => {
    if (vin !== prevVin) {
      setPrevVin(vin);
      if (vin?.length === 17) {
        getVehicleData(vin, values).then((data) => {
          form.setValue("year", data.year);
          form.setValue("make", data.make);
          form.setValue("model", data.model);
          form.setValue("class", data.class_value);
        });
      }
    }
  }, [vin]);

  React.useEffect(() => {
    if (initial) {
      const d = Object.keys(initial);
      d.forEach((k) => form.setValue(k, initial[k]))
    }
  }, [initial]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{mode === "create" ? "Add Trailer" : "Edit Trailer"}</DialogTitle></DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Year</Label>
              <Controller control={control} name="year" render={({ field, fieldState }) => (
                <>
                  <Input type="number" placeholder="Enter..." {...field} />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Make</Label>
              <Controller control={control} name="make" render={({ field, fieldState }) => (
                <>
                  <Input placeholder="Enter..." {...field} />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Controller control={control} name="model" render={({ field, fieldState }) => (
                <>
                  <Input placeholder="Enter..." {...field} />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>VIN</Label>
              <Controller control={control} name="vin" render={({ field, fieldState }) => (
                <>
                  <Input placeholder="Enter..." {...field} maxLength={17} />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Value</Label>
              <Controller control={control} name="value" render={({ field, fieldState }) => (
                <>
                  <Input
                    placeholder="Enter..."
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^\d.-]/g, ""))}
                  />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Body Type</Label>
              <Controller control={control} name="bodyType" render={({ field, fieldState }) => (
                <>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {trailersBodytype.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <DialogClose asChild><Button variant="outline" disabled={isSubmitting}>Cancel</Button></DialogClose>
            <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Proceed with Progressive only?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p>{confirmMessage}</p>
          </div>
          <DialogFooter className="justify-end space-x-2">
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setPendingValues(null); }}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                if (!pendingValues) return;
                try {
                  removeCwNirvanaCarriers();
                  await performSave(pendingValues);
                } catch (err) {
                  const msg =
                    err?.response?.data?.message ??
                    (mode === "create" ? "Failed to create trailer" : "Failed to update trailer");
                  context.notification?.show?.(msg, "error", true, "toast", "x-circle");
                } finally {
                  setConfirmOpen(false);
                  setPendingValues(null);
                }
              }}
            >
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
